import React, { useState, createElement } from 'react';
import { CreditCard, Search, Filter, Download, Eye, RefreshCw, CheckCircle2, Clock, XCircle, AlertTriangle, Pause, Play, MoreVertical, Gift, ArrowUpRight } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { Pagination } from '../ui/Pagination';
interface AdminSubscriptionsFullManagerProps {
  userSubscriptions: any[];
  users: any[];
  setActiveTab: (tab: string) => void;
  setDetailPanel: (panel: any) => void;
  success: (msg: string) => void;
}
const mockSubscriptions = [{
  id: 'SUB-3001',
  user: 'Анна Петрова',
  plan: 'Pro',
  price: '₽990/мес',
  status: 'active',
  start: '01.02.2026',
  end: '01.03.2026',
  nextCharge: '01.04.2026',
  autoRenew: true,
  paymentError: false
}, {
  id: 'SUB-3002',
  user: 'Иван Сидоров',
  plan: 'Premium',
  price: '₽1,990/мес',
  status: 'active',
  start: '15.01.2026',
  end: '15.04.2026',
  nextCharge: '15.04.2026',
  autoRenew: true,
  paymentError: false
}, {
  id: 'SUB-3003',
  user: 'Maria Schmidt',
  plan: 'Pro',
  price: '₽990/мес',
  status: 'trial',
  start: '10.03.2026',
  end: '10.04.2026',
  nextCharge: '10.04.2026',
  autoRenew: true,
  paymentError: false
}, {
  id: 'SUB-3004',
  user: 'Олег Козлов',
  plan: 'Pro',
  price: '₽990/мес',
  status: 'payment_error',
  start: '01.01.2026',
  end: '01.03.2026',
  nextCharge: '—',
  autoRenew: true,
  paymentError: true
}, {
  id: 'SUB-3005',
  user: 'Елена Волкова',
  plan: 'Premium',
  price: '₽1,990/мес',
  status: 'cancelled',
  start: '01.12.2025',
  end: '01.03.2026',
  nextCharge: '—',
  autoRenew: false,
  paymentError: false
}, {
  id: 'SUB-3006',
  user: 'Pierre Dupont',
  plan: 'Pro',
  price: '₽990/мес',
  status: 'paused',
  start: '01.02.2026',
  end: '01.05.2026',
  nextCharge: '—',
  autoRenew: true,
  paymentError: false
}, {
  id: 'SUB-3007',
  user: 'Дмитрий Новиков',
  plan: 'Pro',
  price: '₽990/мес',
  status: 'expired',
  start: '01.10.2025',
  end: '01.01.2026',
  nextCharge: '—',
  autoRenew: false,
  paymentError: false
}];
const subStatusConfig: Record<string, {
  label: string;
  variant: 'success' | 'warning' | 'error' | 'neutral' | 'pro';
}> = {
  active: {
    label: 'Активна',
    variant: 'success'
  },
  trial: {
    label: 'Пробная',
    variant: 'pro'
  },
  paused: {
    label: 'Пауза',
    variant: 'warning'
  },
  cancelled: {
    label: 'Отменена',
    variant: 'neutral'
  },
  expired: {
    label: 'Истекла',
    variant: 'neutral'
  },
  payment_error: {
    label: 'Ошибка оплаты',
    variant: 'error'
  }
};
export function AdminSubscriptionsFullManager({
  userSubscriptions,
  users,
  setActiveTab,
  setDetailPanel,
  success
}: AdminSubscriptionsFullManagerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    plan: 'all',
    autoRenew: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const totalSubs = mockSubscriptions.length;
  const activeSubs = mockSubscriptions.filter(s => s.status === 'active').length;
  const trialSubs = mockSubscriptions.filter(s => s.status === 'trial').length;
  const cancelledSubs = mockSubscriptions.filter(s => s.status === 'cancelled').length;
  const paymentErrors = mockSubscriptions.filter(s => s.paymentError).length;
  const mrr = '₽248K';
  const renderKpi = (label: string, value: string | number, icon: React.ReactNode, bg: string) => <Card padding="sm" hoverable className="rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-24 cursor-pointer group" data-id="element-6277">
      <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center group-hover:scale-110 transition-transform`} data-id="element-6278">
        {icon}
      </div>
      <div data-id="element-6279">
        <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-6280">{label}</div>
        <div className="text-xl font-bold text-slate-900" data-id="element-6281">{value}</div>
      </div>
    </Card>;
  const filtered = mockSubscriptions.filter(s => {
    const matchesSearch = s.user.toLowerCase().includes(searchQuery.toLowerCase()) || s.id.toLowerCase().includes(searchQuery.toLowerCase()) || s.plan.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (filters.status !== 'all' && s.status !== filters.status) return false;
    if (filters.plan !== 'all' && s.plan !== filters.plan) return false;
    if (filters.autoRenew !== 'all') {
      const isAuto = !!s.autoRenew;
      if (filters.autoRenew === 'true' && !isAuto) return false;
      if (filters.autoRenew === 'false' && isAuto) return false;
    }
    return true;
  });
  const handleExport = () => {
    const headers = ['ID', 'Пользователь', 'План', 'Цена', 'Начало', 'Окончание', 'Сл. списание', 'Авто-продление', 'Статус'];
    const rows = filtered.map(s => [s.id, s.user, s.plan, s.price, s.start, s.end, s.nextCharge, s.autoRenew ? 'Да' : 'Нет', subStatusConfig[s.status]?.label || s.status]);
    const csvContent = [headers.join(','), ...rows.map(r => r.map((v: any) => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `subscriptions_export_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    success(`Экспорт ${filtered.length} подписок завершён`);
  };
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const current = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  return <div className="space-y-6" data-id="element-6282">
      <div data-id="element-6283">
        <h2 className="text-xl font-bold text-slate-900" data-id="element-6284">Подписки</h2>
        <p className="text-sm text-slate-500" data-id="element-6285">
          Управление подписками пользователей
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-id="element-6286">
        {renderKpi('Всего', totalSubs, <CreditCard className="w-4 h-4 text-indigo-600" data-id="element-6287" />, 'bg-indigo-50')}
        {renderKpi('Активные', activeSubs, <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-6288" />, 'bg-emerald-50')}
        {renderKpi('Пробные', trialSubs, <Play className="w-4 h-4 text-blue-600" data-id="element-6289" />, 'bg-blue-50')}
        {renderKpi('Отменённые', cancelledSubs, <XCircle className="w-4 h-4 text-slate-600" data-id="element-6290" />, 'bg-slate-100')}
        {renderKpi('Ошибки оплаты', paymentErrors, <AlertTriangle className="w-4 h-4 text-rose-600" data-id="element-6291" />, 'bg-rose-50')}
        {renderKpi('MRR', mrr, <ArrowUpRight className="w-4 h-4 text-emerald-600" data-id="element-6292" />, 'bg-emerald-50')}
      </div>

      <Card padding="sm" className="rounded-xl shadow-none border-slate-200" data-id="element-6293">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-6294">
          <div className="relative flex-1 max-w-md" data-id="element-6295">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-6296" />
            <input type="text" placeholder="Поиск по пользователю, ID, плану..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6297" />
          </div>
          <div className="flex gap-2" data-id="element-6298">
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Filter className="w-4 h-4" data-id="element-6300" />} onClick={() => setShowFilters(!showFilters)} data-id="element-6299">
              Фильтры
            </Button>
            <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-6302" />} onClick={handleExport} data-id="element-6301">
              CSV
            </Button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && <div className="mt-4 pt-4 border-t border-slate-100 space-y-4 animate-in slide-in-from-top-2" data-id="element-6303">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3" data-id="element-6304">
              <div data-id="element-6305">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6306">
                  Статус
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.status} onChange={e => setFilters({
              ...filters,
              status: e.target.value
            })} data-id="element-6307">
                  <option value="all" data-id="element-6308">Все</option>
                  <option value="active" data-id="element-6309">Активные</option>
                  <option value="trial" data-id="element-6310">Пробные</option>
                  <option value="paused" data-id="element-6311">На паузе</option>
                  <option value="cancelled" data-id="element-6312">Отмененные</option>
                  <option value="expired" data-id="element-6313">Истекшие</option>
                  <option value="payment_error" data-id="element-6314">Ошибка оплаты</option>
                </select>
              </div>
              <div data-id="element-6315">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6316">
                  План
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.plan} onChange={e => setFilters({
              ...filters,
              plan: e.target.value
            })} data-id="element-6317">
                  <option value="all" data-id="element-6318">Все</option>
                  <option value="Pro" data-id="element-6319">Pro</option>
                  <option value="Premium" data-id="element-6320">Premium</option>
                </select>
              </div>
              <div data-id="element-6321">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6322">
                  Авто-продление
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.autoRenew} onChange={e => setFilters({
              ...filters,
              autoRenew: e.target.value
            })} data-id="element-6323">
                  <option value="all" data-id="element-6324">Все</option>
                  <option value="true" data-id="element-6325">Включено</option>
                  <option value="false" data-id="element-6326">Выключено</option>
                </select>
              </div>
              <div className="flex items-end" data-id="element-6327">
                <Button variant="ghost" size="sm" onClick={() => setFilters({
              status: 'all',
              plan: 'all',
              autoRenew: 'all'
            })} data-id="element-6328">
                  Сбросить
                </Button>
              </div>
            </div>
          </div>}
      </Card>

      <Card padding="none" className="rounded-xl shadow-none border-slate-200 overflow-hidden" data-id="element-6329">
        <div className="overflow-x-auto" data-id="element-6330">
          <table className="w-full text-left" data-id="element-6331">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-6332">
              <tr data-id="element-6333">
                <th className="px-4 py-3 font-medium" data-id="element-6334">Sub ID</th>
                <th className="px-4 py-3 font-medium" data-id="element-6335">Пользователь</th>
                <th className="px-4 py-3 font-medium" data-id="element-6336">План</th>
                <th className="px-4 py-3 font-medium" data-id="element-6337">Цена</th>
                <th className="px-4 py-3 font-medium" data-id="element-6338">Начало</th>
                <th className="px-4 py-3 font-medium" data-id="element-6339">Окончание</th>
                <th className="px-4 py-3 font-medium" data-id="element-6340">Сл. списание</th>
                <th className="px-4 py-3 font-medium" data-id="element-6341">Авто</th>
                <th className="px-4 py-3 font-medium" data-id="element-6342">Статус</th>
                <th className="px-4 py-3 text-right font-medium" data-id="element-6343">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6344">
              {current.map(s => <tr key={s.id} className={`hover:bg-slate-50 transition-colors ${s.paymentError ? 'bg-rose-50/30' : ''}`} data-id="element-6345">
                  <td className="px-4 py-3" data-id="element-6346">
                    <span className="text-xs font-bold text-indigo-600 cursor-pointer hover:underline" onClick={() => setDetailPanel({
                  type: 'subscription_full',
                  title: `Подписка ${s.id}`,
                  data: s
                })} data-id="element-6347">
                      {s.id}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900 cursor-pointer hover:text-indigo-600" onClick={() => setDetailPanel({
                type: 'user_full',
                title: 'Профиль пользователя',
                data: {
                  name: s.user
                }
              })} data-id="element-6348">
                    {s.user}
                  </td>
                  <td className="px-4 py-3" data-id="element-6349">
                    <Badge variant={s.plan === 'Premium' ? 'top' : 'pro'} size="sm" data-id="element-6350">
                      {s.plan}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900" data-id="element-6351">
                    {s.price}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600" data-id="element-6352">
                    {s.start}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600" data-id="element-6353">{s.end}</td>
                  <td className="px-4 py-3 text-xs text-slate-600" data-id="element-6354">
                    {s.nextCharge}
                  </td>
                  <td className="px-4 py-3" data-id="element-6355">
                    {s.autoRenew ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-6356" /> : <XCircle className="w-4 h-4 text-slate-300" data-id="element-6357" />}
                  </td>
                  <td className="px-4 py-3" data-id="element-6358">
                    <Badge variant={subStatusConfig[s.status]?.variant || 'neutral'} size="sm" data-id="element-6359">
                      {subStatusConfig[s.status]?.label || s.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-6360">
                    <div className="flex items-center justify-end gap-1" data-id="element-6361">
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-indigo-600" icon={<Eye className="w-3.5 h-3.5" data-id="element-6363" />} onClick={() => setDetailPanel({
                    type: 'subscription_full',
                    title: `Подписка ${s.id}`,
                    data: s
                  })} data-id="element-6362" />
                      {s.status === 'active' && <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-amber-600" icon={<Pause className="w-3.5 h-3.5" data-id="element-6365" />} onClick={() => success(`Подписка ${s.id} приостановлена`)} title="Приостановить" data-id="element-6364" />}
                      {s.status === 'paused' && <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-emerald-600" icon={<Play className="w-3.5 h-3.5" data-id="element-6367" />} onClick={() => success(`Подписка ${s.id} возобновлена`)} title="Возобновить" data-id="element-6366" />}
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-purple-600" icon={<Gift className="w-3.5 h-3.5" data-id="element-6369" />} onClick={() => success(`Бонусный период выдан для ${s.id}`)} title="Бонусный период" data-id="element-6368" />
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center" data-id="element-6370">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-6371" />
          </div>}
      </Card>
    </div>;
}